import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RootState } from '../../redux';
import './SharedLinks.css';

interface SubWorkspace {
  id: string;
  roleId: number;
}

interface SharePlaybookDialogProps {
  open: boolean;
  onClose: () => void;
  playBookUid: string | null;
}

const SharePlaybookDialog = ({ open, onClose, playBookUid }: SharePlaybookDialogProps) => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const locationState = useLocation().state;
  const [searchParams] = useSearchParams();
  const workspaceId = searchParams.get('workspaceId');

  // Get tab state from Redux
  const tabState = useSelector((state: RootState) => state.tabState);

  // Filter out hidden tabs
  const tabOptions = useMemo(() => {
    return tabState.tabList
      .filter((tabId: number) => !tabState.tabs[tabId]?.hidden)
      .map((tabId: number) => ({
        tabId,
        tabName: tabState.tabs[tabId]?.tabName || `Tab ${tabId}`,
      }));
  }, [tabState.tabList, tabState.tabs]);

  // Default to the tabName already in the URL, or the first available tab
  const [selectedTabName, setSelectedTabName] = useState('');

  // Build and memoize the share URL — sets/replaces only the 'tabName' param
  const shareUrl = useMemo(() => {
    try {
      const url = new URL(window.location.href);
      if (selectedTabName) {
        url.searchParams.set('tabName', selectedTabName);
      } else {
        url.searchParams.delete('tabName');
      }
      const search = Array.from(url.searchParams.entries())
        .map(([key, val]) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
        .join('&');
      return `${url.origin}${url.pathname}${search ? '?' + search : ''}${url.hash}`;
    } catch {
      return window.location.href;
    }
  }, [selectedTabName]);

  const canGrantPermission = useMemo(() => {
    const playbookId = searchParams.get('playbookId');
    const subWorkspacesRaw = sessionStorage.getItem('subWorkspaces');

    if (!playbookId || !subWorkspacesRaw) {
      return false;
    }

    try {
      const subWorkspaces: SubWorkspace[] = JSON.parse(subWorkspacesRaw);
      const matchedWorkspace = subWorkspaces.find((ws) => ws.id === playbookId);

      if (matchedWorkspace?.roleId) {
        const { roleId } = matchedWorkspace;
        return roleId >= 1 && roleId <= 4;
      }
    } catch (error) {
      return false;
    }

    return false;
  }, [searchParams]);

  const handleGrantPermission = () => {
    const workSpaceId = localStorage.getItem('workspaceId');
    navigate(`/content/access/${playBookUid}`, {
      state: {
        parentId: locationState?.parentId || workspaceId,
        workspaceId: workSpaceId,
      },
    });
    onClose();
  };

  const copyToClipboard = (text: string): void => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 3000);
      })
      .catch((err) => {
        console.error('Could not copy text: ', err);
      });
  };

  return (
    <Dialog
      id="share-dialog"
      open={open}
      PaperProps={{
        sx: {
          minHeight: '20%',
          width: '55rem',
          border: '1px solid transparent',
        },
      }}
    >
      <div className="shared-links-header-close-container">
        <div className="expire-link-close">
          <Tooltip
            title="Close"
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: '12px',
                },
              },
            }}
          >
            <CloseIcon
              onClick={onClose}
              sx={{
                fontSize: 'large',
                color: '#545454',
                cursor: 'pointer',
                '&:hover': {
                  color: 'red',
                },
              }}
            />
          </Tooltip>
        </div>

        <div style={{ textAlign: 'center', width: '100%', marginTop: '20px' }}>
          <DialogTitle
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <h2>Share Dashboard</h2>&nbsp;
            <img
              key={'Share'}
              style={{
                height: '26px',
                width: '26px',
                padding: '0.2rem',
              }}
              src={'/share.png'}
              alt={'Share Playbook'}
            />
          </DialogTitle>
        </div>
      </div>

      <DialogContent
        sx={{
          width: '100%',
          border: '1px solid transparent',
        }}
      >
        {canGrantPermission && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
              marginBottom: '15px',
            }}
          >
            <button
              className="create-workspace"
              style={{
                width: 'auto',
                padding: '0 10px',
              }}
              onClick={handleGrantPermission}
            >
              &nbsp;Grant Permission
            </button>
          </div>
        )}

        <div className="link-expire-date-copy-container" style={{ width: '100%' }}>
          <div
            className="link-expire-date"
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              width: '100%',
            }}
          >
            <div
              className="link-expire-span"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <span className="link-expire-span-link" style={{ fontWeight: 'bold' }}>
                Link :
              </span>

              {tabOptions.length > 0 && (
                <FormControl size="small" sx={{ minWidth: '150px' }}>
                  <InputLabel sx={{ fontSize: '13px', top: '-4px' }}>Opening Tab</InputLabel>
                  <Select
                    value={selectedTabName}
                    label="Opening Tab"
                    onChange={(e) => setSelectedTabName(e.target.value)}
                    sx={{ fontSize: '13px', height: '30px' }}
                  >
                    <MenuItem
                      value=""
                      sx={{ fontSize: '13px', fontStyle: 'italic', color: 'text.secondary' }}
                    >
                      No specific tab
                    </MenuItem>
                    {tabOptions.map((tab: any) => (
                      <MenuItem key={tab.tabId} value={tab.tabName} sx={{ fontSize: '13px' }}>
                        {tab.tabName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <div
                className="copy-container"
                style={{ position: 'relative' }}
                onClick={() => copyToClipboard(shareUrl)}
              >
                {copied ? (
                  <div className="copied">
                    <img className="tick-icon" src="/tick-icon.png" alt="Tick Icon" />
                    <span>copied</span>
                  </div>
                ) : (
                  <div className="copy">
                    <img src="/copy-image2.png" alt="Copy Icon" />
                    <span>copy</span>
                  </div>
                )}
              </div>
            </div>

            <div
              className="share-link-copy-paste-container"
              style={{
                marginTop: '8px',
                justifyContent: 'flex-start',
                wordBreak: 'break-all',
              }}
            >
              <span>{shareUrl}</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SharePlaybookDialog;
